/**
 * LERA Smart Purchase Bar — Sprint S8.11 / S8.11A / S8.11B / S8.11C / S8.11D
 * LeraSmartPurchaseController: orchestration only.
 * Reuses product-form submit + pub/sub. No /cart/add. No second form.
 * S8.11A: portals into #LeraViewportLayer.
 * S8.11B: mobile activation gate, success transform, auto-dismiss lock.
 * S8.11C: integrated variant select — mirrors the main picker and drives it
 *         through the native change flow (variant-selects → pub/sub).
 * S8.11D: mobile 3-row layout support + chat widget clearance (measured,
 *         configurable via data-chat-selector / data-chat-offset-extra).
 */

(function () {
  const PURCHASE = {
    IDLE: 'IDLE',
    VISIBLE: 'VISIBLE',
    ADDING: 'ADDING',
    SUCCESS: 'SUCCESS',
    ERROR: 'ERROR',
    SELECT_VARIANT: 'SELECT_VARIANT',
    SOLD_OUT: 'SOLD_OUT',
  };

  const VISIBILITY = {
    BUYBOX_VISIBLE: 'BUYBOX_VISIBLE',
    BUYBOX_OUT: 'BUYBOX_OUT',
  };

  /** Mobile-only: bar stays hidden until the user has scrolled at least this far. */
  const MOBILE_SCROLL_THRESHOLD = 400;
  const MOBILE_MQ = '(max-width: 749px)';
  const DEFAULT_SUCCESS_MS = 2800;
  const DISMISS_FALLBACK_MS = 420;

  /**
   * S8.11D — chat widget containers to lift above the bar.
   * Override per instance via data-chat-selector on the element.
   */
  const DEFAULT_CHAT_SELECTOR = [
    'shopify-agent',
    '#shopify-chat',
    '#ShopifyChat',
    '#gorgias-chat-container',
    '#tidio-chat',
    '#crisp-chatbox',
    '#chat-button',
    '.shopify-chat-frame',
  ].join(', ');
  const DEFAULT_CHAT_EXTRA_PX = 8;
  const CHAT_TRANSITION = 'transform 240ms ease';
  const CHAT_RESTORE_CLEANUP_MS = 300;

  function formatMoney(cents, format) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, format);
    }
    const value = (Number(cents) / 100).toFixed(2);
    return (format || '{{amount}}').replace(/\{\{\s*amount\s*\}\}/g, value);
  }

  class LeraSmartPurchaseController {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.productFormId = root.dataset.productFormId;
      this.quantityId = root.dataset.quantityId;
      this.priceId = root.dataset.priceId;
      this.submitId = root.dataset.submitId;
      this.buyboxSelector = root.dataset.buyboxSelector || '.lera-pi-conversion-cluster';
      this.moneyFormat = root.dataset.moneyFormat || '';
      this.successMs = Number(root.dataset.successMs || DEFAULT_SUCCESS_MS);
      this.labels = {
        atc: root.dataset.labelAtc || 'Add to cart',
        adding: root.dataset.labelAdding || 'Adding...',
        soldOut: root.dataset.labelSoldOut || 'Sold out',
        unavailable: root.dataset.labelUnavailable || 'Unavailable',
        select: root.dataset.labelSelect || 'Select model',
        added: root.dataset.labelAdded || 'Product added',
        continue: root.dataset.labelContinue || 'Continue shopping',
        checkout: root.dataset.labelCheckout || 'Checkout',
        inCart: root.dataset.labelInCart || 'In cart',
        subtotal: root.dataset.labelSubtotal || 'Subtotal',
      };

      this.state = {
        purchase: PURCHASE.IDLE,
        visibility: VISIBILITY.BUYBOX_VISIBLE,
        mobileScrollReady: false,
        postSuccessLock: false,
      };

      this.unsubscribers = [];
      this.observer = null;
      this.priceObserver = null;
      this.successTimer = null;
      this.dismissTimer = null;
      this.bound = {};
      this._dismissing = false;
      this._chatShifted = false;
      this._lastBarShown = false;
      this.chatSelector = root.dataset.chatSelector || DEFAULT_CHAT_SELECTOR;
      this.chatExtraPx = Number(root.dataset.chatOffsetExtra);
      if (!Number.isFinite(this.chatExtraPx)) this.chatExtraPx = DEFAULT_CHAT_EXTRA_PX;

      this.els = {
        purchasePanel: root.querySelector('[data-lera-spb-panel="purchase"]'),
        successPanel: root.querySelector('[data-lera-spb-panel="success"]'),
        media: root.querySelector('[data-lera-spb-media]'),
        title: root.querySelector('[data-lera-spb-title]'),
        variant: root.querySelector('[data-lera-spb-variant]'),
        price: root.querySelector('[data-lera-spb-price]'),
        compare: root.querySelector('[data-lera-spb-compare]'),
        qtyValue: root.querySelector('[data-lera-spb-qty-value]'),
        qtyMinus: root.querySelector('[data-lera-spb-qty-minus]'),
        qtyPlus: root.querySelector('[data-lera-spb-qty-plus]'),
        cta: root.querySelector('[data-lera-spb-cta]'),
        ctaLabel: root.querySelector('[data-lera-spb-cta-label]'),
        live: root.querySelector('[data-lera-spb-live]'),
        cartCount: root.querySelector('[data-lera-spb-cart-count]'),
        cartSubtotal: root.querySelector('[data-lera-spb-cart-subtotal]'),
        continueBtn: root.querySelector('[data-lera-spb-continue]'),
        checkout: root.querySelector('[data-lera-spb-checkout]'),
        variantSelect: root.querySelector('[data-lera-spb-variant-select]'),
      };
    }

    isMobile() {
      return window.matchMedia(MOBILE_MQ).matches;
    }

    passesMobileGate() {
      if (!this.isMobile()) return true;
      return this.state.mobileScrollReady;
    }

    /**
     * Lift the bar above Shopify draft preview chrome when present.
     * Cheap DOM read on show — no observers.
     */
    syncChromeOffset() {
      const preview = document.getElementById('PBarNextFrameWrapper');
      const height = preview ? Math.round(preview.getBoundingClientRect().height) : 0;
      this.root.style.setProperty('--lera-spb-chrome-offset', height > 0 ? `${height}px` : '0px');
    }

    /**
     * Move this bar into the global viewport layer once.
     * Does not clone. Does not recreate markup. Safe if already portaled.
     */
    portalToViewportLayer() {
      if (this.root.dataset.leraPortaled === '1' && this.root.closest('#LeraViewportLayer')) {
        return;
      }

      const sectionId = this.sectionId;
      const layerApi = window.LeraViewportLayer;
      const layer =
        (layerApi && typeof layerApi.ensure === 'function' && layerApi.ensure()) ||
        document.getElementById('LeraViewportLayer');

      if (!layer) return;

      layer.querySelectorAll('lera-smart-purchase-bar.lera-spb, .lera-spb[data-section-id]').forEach((existing) => {
        if (existing === this.root) return;
        if (String(existing.dataset.sectionId) !== String(sectionId)) return;
        existing._leraSpbController?.destroy?.();
        existing.remove();
      });

      if (layerApi && typeof layerApi.portal === 'function') {
        layerApi.portal(this.root, { slot: 'floating' });
      } else if (this.root.parentElement !== layer) {
        layer.appendChild(this.root);
        this.root.setAttribute('data-lera-portaled', '1');
        this.root.setAttribute('data-lera-viewport-slot', 'floating');
      }

      this.root.classList.add('lera-spb--viewport');
    }

    init() {
      if (this.root.dataset.leraSpbReady === '1') return;
      this.root.dataset.leraSpbReady = '1';

      this.portalToViewportLayer();
      this.bindUi();
      this.bindMobileScrollGate();
      this.observeBuyBox();
      this.subscribeThemeEvents();
      this.observePriceFallback();
      this.syncFromDom();
      this.render();
    }

    destroy() {
      this.unsubscribers.forEach((off) => {
        try {
          off();
        } catch (e) {
          /* noop */
        }
      });
      this.unsubscribers = [];
      this.observer?.disconnect();
      this.priceObserver?.disconnect();
      clearTimeout(this.successTimer);
      clearTimeout(this.dismissTimer);
      if (this.bound.onScroll) {
        window.removeEventListener('scroll', this.bound.onScroll);
      }
      if (this.bound.onDismissEnd) {
        this.root.removeEventListener('transitionend', this.bound.onDismissEnd);
      }
      if (this.bound.onQtyChange && this.getQtyInput()) {
        this.getQtyInput().removeEventListener('change', this.bound.onQtyChange);
      }
      if (this.bound.onVariantSelect) {
        this.els.variantSelect?.removeEventListener('change', this.bound.onVariantSelect);
      }
      this.restoreChatClearance();
      this.root.dataset.leraSpbReady = '0';
    }

    /**
     * S8.11D — lift the chat widget container(s) above the visible bar.
     * Offset is measured from the bar's rendered height each time it shows,
     * so it adapts to any layout/breakpoint without hardcoded values.
     */
    getChatElements() {
      let matches;
      try {
        matches = document.querySelectorAll(this.chatSelector);
      } catch (e) {
        return [];
      }
      return [...matches].filter((el) => !this.root.contains(el));
    }

    applyChatClearance() {
      window.requestAnimationFrame(() => {
        if (!this._lastBarShown) return;
        const height = Math.ceil(this.root.getBoundingClientRect().height);
        if (!height) return;

        const offset = height + this.chatExtraPx;
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        this.getChatElements().forEach((el) => {
          if (el.dataset.leraSpbChatShifted !== '1') {
            el.dataset.leraSpbChatShifted = '1';
            el.dataset.leraSpbPrevTransform = el.style.transform || '';
            el.dataset.leraSpbPrevTransition = el.style.transition || '';
          }
          el.style.transition = reduced ? 'none' : CHAT_TRANSITION;
          el.style.transform = `translateY(-${offset}px)`;
        });
        this._chatShifted = true;
      });
    }

    restoreChatClearance() {
      if (!this._chatShifted) return;
      this._chatShifted = false;

      this.getChatElements().forEach((el) => {
        if (el.dataset.leraSpbChatShifted !== '1') return;
        el.style.transform = el.dataset.leraSpbPrevTransform || '';
        window.setTimeout(() => {
          if (el.dataset.leraSpbChatShifted !== '1' || this._chatShifted) return;
          el.style.transition = el.dataset.leraSpbPrevTransition || '';
          delete el.dataset.leraSpbChatShifted;
          delete el.dataset.leraSpbPrevTransform;
          delete el.dataset.leraSpbPrevTransition;
        }, CHAT_RESTORE_CLEANUP_MS);
      });
    }

    syncChatClearance(visible) {
      if (visible) {
        this.applyChatClearance();
      } else {
        this.restoreChatClearance();
      }
    }

    setState(partial) {
      this.state = { ...this.state, ...partial };
      this.render();
    }

    bindUi() {
      this.bound.onQtyChange = () => this.syncQuantityMirror();
      this.bound.onMinus = () => this.stepQuantity(-1);
      this.bound.onPlus = () => this.stepQuantity(1);
      this.bound.onCta = () => this.handleCta();
      this.bound.onContinue = () => this.dismissSuccess();
      this.bound.onVariantSelect = () => this.handleVariantSelectChange();

      this.els.qtyMinus?.addEventListener('click', this.bound.onMinus);
      this.els.qtyPlus?.addEventListener('click', this.bound.onPlus);
      this.els.cta?.addEventListener('click', this.bound.onCta);
      this.els.continueBtn?.addEventListener('click', this.bound.onContinue);
      this.els.variantSelect?.addEventListener('change', this.bound.onVariantSelect);

      const qty = this.getQtyInput();
      qty?.addEventListener('change', this.bound.onQtyChange);
    }

    /**
     * Single passive scroll listener — updates mobileScrollReady only when the boolean flips.
     * Desktop ignores the threshold (passesMobileGate always true).
     */
    bindMobileScrollGate() {
      this.bound.onScroll = () => {
        if (!this.isMobile()) return;
        const ready = window.scrollY >= MOBILE_SCROLL_THRESHOLD;
        if (ready === this.state.mobileScrollReady) return;
        this.state.mobileScrollReady = ready;
        this.render();
      };
      window.addEventListener('scroll', this.bound.onScroll, { passive: true });
      this.bound.onScroll();
    }

    getProductInfo() {
      return (
        document.getElementById(`MainProduct-${this.sectionId}`) ||
        document.querySelector(`product-info[data-section="${this.sectionId}"]`)
      );
    }

    getProductForm() {
      return (
        document.querySelector(`product-form[data-section-id="${this.sectionId}"]`) ||
        document.getElementById(this.productFormId)?.closest('product-form') ||
        null
      );
    }

    getFormEl() {
      return document.getElementById(this.productFormId);
    }

    getSubmitButton() {
      return document.getElementById(this.submitId);
    }

    getQtyInput() {
      return document.getElementById(this.quantityId);
    }

    getPriceNode() {
      return document.getElementById(this.priceId);
    }

    getBuyBox() {
      const info = this.getProductInfo();
      return (info && info.querySelector(this.buyboxSelector)) || document.querySelector(this.buyboxSelector);
    }

    observeBuyBox() {
      const buybox = this.getBuyBox();
      if (!buybox || !('IntersectionObserver' in window)) {
        this.setState({
          visibility: VISIBILITY.BUYBOX_OUT,
          purchase: this.derivePurchaseState(),
        });
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          const visible = Boolean(entry?.isIntersecting && entry.intersectionRatio > 0);
          const visibility = visible ? VISIBILITY.BUYBOX_VISIBLE : VISIBILITY.BUYBOX_OUT;

          if (visible) {
            // Returning to the Buy Box clears the post-ATC lock so the bar can show again later.
            if (
              this.state.visibility === visibility &&
              !this.state.postSuccessLock &&
              this.state.purchase !== PURCHASE.SUCCESS &&
              this.state.purchase !== PURCHASE.ADDING
            ) {
              return;
            }

            if (this.state.purchase === PURCHASE.SUCCESS || this.state.purchase === PURCHASE.ADDING) {
              this.setState({ visibility, postSuccessLock: false });
              return;
            }

            this.setState({
              visibility,
              purchase: PURCHASE.IDLE,
              postSuccessLock: false,
            });
            return;
          }

          // Buy box out of view
          if (visibility === this.state.visibility && this.state.purchase !== PURCHASE.SUCCESS) {
            // Still re-render when mobile gate may have changed via scroll (handled separately).
            if (!this.state.postSuccessLock) return;
          }

          if (this.state.purchase === PURCHASE.SUCCESS || this.state.purchase === PURCHASE.ADDING) {
            this.setState({ visibility });
            return;
          }

          if (this.state.postSuccessLock) {
            this.setState({ visibility, purchase: PURCHASE.IDLE });
            return;
          }

          this.setState({
            visibility,
            purchase: this.derivePurchaseState(),
          });
        },
        { root: null, threshold: 0, rootMargin: '0px' }
      );

      this.observer.observe(buybox);
    }

    subscribeThemeEvents() {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribers.push(
          subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (String(event?.data?.sectionId) !== String(this.sectionId)) return;
            this.syncFromVariantEvent(event);
            if (
              this.state.visibility === VISIBILITY.BUYBOX_OUT &&
              this.state.purchase !== PURCHASE.SUCCESS &&
              !this.state.postSuccessLock
            ) {
              this.setState({ purchase: this.derivePurchaseState() });
            } else {
              this.renderPurchaseChrome();
            }
          })
        );

        this.unsubscribers.push(
          subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            if (event?.source !== 'product-form') return;
            this.onCartSuccess(event);
          })
        );

        this.unsubscribers.push(
          subscribe(PUB_SUB_EVENTS.cartError, (event) => {
            if (event?.source !== 'product-form') return;
            this.setState({ purchase: PURCHASE.ERROR });
            this.announce(event?.message || this.labels.unavailable);
            window.setTimeout(() => {
              if (this.state.purchase === PURCHASE.ERROR) {
                this.setState({
                  purchase: this.shouldShowPurchaseChrome()
                    ? this.derivePurchaseState()
                    : PURCHASE.IDLE,
                });
              }
            }, 2200);
          })
        );
      }
    }

    observePriceFallback() {
      const priceNode = this.getPriceNode();
      if (!priceNode || !('MutationObserver' in window)) return;
      this.priceObserver = new MutationObserver(() => {
        this.syncPriceFromDom();
      });
      this.priceObserver.observe(priceNode, { childList: true, subtree: true, characterData: true });
    }

    derivePurchaseState() {
      if (this.isSelectionIncomplete()) return PURCHASE.SELECT_VARIANT;
      const submit = this.getSubmitButton();
      if (!submit) return PURCHASE.VISIBLE;
      if (submit.hasAttribute('disabled') || submit.getAttribute('aria-disabled') === 'true') {
        const label = (submit.querySelector('[data-lera-atc-label]') || submit).textContent || '';
        if (/sold|agotad/i.test(label) || submit.classList.contains('sold-out')) return PURCHASE.SOLD_OUT;
        if (/unavail|no dispon/i.test(label)) return PURCHASE.SELECT_VARIANT;
        return PURCHASE.SOLD_OUT;
      }
      return PURCHASE.VISIBLE;
    }

    isSelectionIncomplete() {
      const info = this.getProductInfo();
      if (!info) return false;

      const fieldsets = info.querySelectorAll('variant-selects fieldset');
      for (const fieldset of fieldsets) {
        const radios = fieldset.querySelectorAll('input[type="radio"]');
        if (radios.length && ![...radios].some((input) => input.checked)) return true;
      }

      const selects = info.querySelectorAll('variant-selects select');
      for (const select of selects) {
        if (!select.value) return true;
      }

      if (window.LeraSmartSelector?.getState) {
        try {
          const state = window.LeraSmartSelector.getState(this.sectionId);
          if (state && state.complete === false) return true;
        } catch (e) {
          /* noop */
        }
      }

      return false;
    }

    findIncompleteControl() {
      const info = this.getProductInfo();
      if (!info) return null;

      for (const fieldset of info.querySelectorAll('variant-selects fieldset')) {
        const radios = fieldset.querySelectorAll('input[type="radio"]');
        if (radios.length && ![...radios].some((input) => input.checked)) {
          return radios[0];
        }
      }

      for (const select of info.querySelectorAll('variant-selects select')) {
        if (!select.value) return select;
      }

      const smartTrigger = info.querySelector(
        'lera-smart-selector [aria-expanded], .lera-smart-selector__trigger, .lera-premium-dropdown__trigger'
      );
      return smartTrigger || info.querySelector('variant-selects');
    }

    syncFromVariantEvent(event) {
      const variant = event?.data?.variant;
      if (variant) {
        if (this.els.variant) {
          const label = variant.title && variant.title !== 'Default Title' ? variant.title : '';
          this.els.variant.textContent = label;
          this.els.variant.classList.toggle('is-empty', !label);
        }
        this.syncMediaFromVariant(variant);
        this.syncVariantSelectMirror(variant);
      }
      this.syncPriceFromDom();
      this.syncQuantityMirror();
    }

    /**
     * S8.11C — PDP → bar. Programmatic value set fires no change event,
     * so this cannot loop back into handleVariantSelectChange().
     */
    syncVariantSelectMirror(variant) {
      const select = this.els.variantSelect;
      if (!select || variant?.id == null) return;
      const value = String(variant.id);
      if (select.value !== value) select.value = value;
    }

    getVariantSelectsRoot() {
      const info = this.getProductInfo();
      return (
        info?.querySelector('variant-selects') ||
        document.querySelector(`#variant-selects-${this.sectionId}`)
      );
    }

    /**
     * S8.11C — bar → PDP. Sets the main picker controls to the chosen
     * variant's options, then dispatches one bubbling `change` so Dawn's
     * variant-selects runs its normal flow (metadata, pub/sub, re-render).
     * No duplicated variant logic lives here.
     */
    handleVariantSelectChange() {
      const select = this.els.variantSelect;
      const chosen = select?.selectedOptions?.[0];
      if (!chosen) return;

      const values = [chosen.dataset.option1, chosen.dataset.option2, chosen.dataset.option3];
      const variantRoot = this.getVariantSelectsRoot();

      if (variantRoot) {
        const groups = variantRoot.querySelectorAll('fieldset, .product-form__input--dropdown');
        let lastChanged = null;

        groups.forEach((group, index) => {
          const value = values[index];
          if (value == null || value === '') return;

          const nativeSelect = group.querySelector('select');
          if (nativeSelect) {
            const next = [...nativeSelect.options].find((opt) => opt.value === value);
            if (!next) return;
            const prev = [...nativeSelect.options].find((opt) => opt.getAttribute('selected'));
            if (nativeSelect.value !== value) nativeSelect.value = value;
            // Keep Dawn's option[selected] metadata coherent for groups that
            // won't receive the change event (selectedOptionValues reads it).
            if (prev !== next) {
              prev?.removeAttribute('selected');
              next.setAttribute('selected', 'selected');
              lastChanged = nativeSelect;
            }
            return;
          }

          const radios = group.querySelectorAll('input[type="radio"]');
          for (const radio of radios) {
            if (radio.value === value) {
              if (!radio.checked) {
                radio.checked = true;
                lastChanged = radio;
              }
              break;
            }
          }
        });

        if (lastChanged) {
          lastChanged.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return;
      }

      // Fallback (no picker in DOM): update the product form's id input.
      const idInput = this.getFormEl()?.querySelector('input[name="id"]');
      if (idInput) {
        idInput.value = chosen.value;
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    syncMediaFromVariant(variant) {
      const mediaId = variant?.featured_media?.id;
      const info = this.getProductInfo();
      let img = null;
      if (mediaId && info) {
        img = info.querySelector(
          `[data-media-id$="-${mediaId}"] img, media-gallery [data-media-id*="${mediaId}"] img`
        );
      }
      if (!img) {
        img = info?.querySelector('media-gallery .is-active img, .product__media-item.is-active img, .lera-gallery img');
      }
      const target = this.els.media?.querySelector('img.lera-spb__image');
      if (img && target) {
        target.src = img.currentSrc || img.src;
        if (img.srcset) target.srcset = img.srcset;
        target.alt = img.alt || this.els.title?.textContent || '';
      }
    }

    syncFromDom() {
      this.syncPriceFromDom();
      this.syncQuantityMirror();
      this.syncMediaFromVariant({});
    }

    syncPriceFromDom() {
      const priceNode = this.getPriceNode();
      if (!priceNode || !this.els.price) return;

      const current =
        priceNode.querySelector('.lera-pi-price-core__current-value') ||
        priceNode.querySelector('.price-item--sale') ||
        priceNode.querySelector('.price-item--regular') ||
        priceNode.querySelector('.price__regular .price-item');

      const compare =
        priceNode.querySelector('.lera-pi-price-core__compare-value') ||
        priceNode.querySelector('s.price-item') ||
        priceNode.querySelector('.price__sale s') ||
        priceNode.querySelector('s');

      if (current) this.els.price.textContent = current.textContent.trim();

      if (this.els.compare) {
        const compareText = compare?.textContent?.trim();
        const hasCompare = Boolean(compareText) && compareText !== this.els.price.textContent;
        this.els.compare.textContent = hasCompare ? compareText : '';
        this.els.compare.classList.toggle('is-hidden', !hasCompare);
      }
    }

    syncQuantityMirror() {
      const input = this.getQtyInput();
      if (!input || !this.els.qtyValue) return;
      this.els.qtyValue.textContent = input.value || '1';
    }

    stepQuantity(direction) {
      const input = this.getQtyInput();
      if (!input) return;
      const host = input.closest('quantity-input');
      if (direction < 0) {
        host?.querySelector('button[name="minus"]')?.click();
        if (!host) {
          input.stepDown();
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } else {
        host?.querySelector('button[name="plus"]')?.click();
        if (!host) {
          input.stepUp();
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      window.requestAnimationFrame(() => this.syncQuantityMirror());
    }

    handleCta() {
      if (this.state.purchase === PURCHASE.SELECT_VARIANT) {
        this.focusIncompleteSelector();
        return;
      }
      if (this.state.purchase === PURCHASE.SOLD_OUT || this.state.purchase === PURCHASE.ADDING) return;

      const form = this.getFormEl();
      const productForm = this.getProductForm();
      const submit = this.getSubmitButton();

      this._dismissing = false;
      this.root.classList.remove('is-dismissing');
      this.setState({ purchase: PURCHASE.ADDING });
      this.announce(this.labels.adding);

      if (form && typeof form.requestSubmit === 'function') {
        form.requestSubmit(submit || undefined);
        return;
      }
      if (submit) {
        submit.click();
        return;
      }
      productForm?.querySelector('form')?.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }

    focusIncompleteSelector() {
      const control = this.findIncompleteControl();
      const buybox = this.getBuyBox();
      const target = control || buybox;
      if (!target) return;

      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      buybox?.classList.add('lera-spb-highlight');
      window.setTimeout(() => buybox?.classList.remove('lera-spb-highlight'), 1600);

      window.setTimeout(() => {
        try {
          control?.focus?.({ preventScroll: true });
        } catch (e) {
          control?.focus?.();
        }
      }, 280);

      this.announce(this.labels.select);
    }

    onCartSuccess(event) {
      clearTimeout(this.successTimer);
      clearTimeout(this.dismissTimer);
      this._dismissing = false;
      this.root.classList.remove('is-dismissing');

      const cartData = event?.cartData || {};
      const itemCount = cartData.item_count ?? cartData.items?.length ?? '';
      const subtotalCents = cartData.items_subtotal_price ?? cartData.total_price;

      if (this.els.cartCount) {
        this.els.cartCount.textContent = itemCount !== '' ? `${this.labels.inCart}: ${itemCount}` : '';
      }
      if (this.els.cartSubtotal && subtotalCents != null) {
        this.els.cartSubtotal.textContent = `${this.labels.subtotal}: ${formatMoney(subtotalCents, this.moneyFormat)}`;
      }

      // Keep success visible even if the mobile scroll gate would hide purchase chrome.
      this.setState({
        purchase: PURCHASE.SUCCESS,
      });
      this.announce(this.labels.added);

      this.successTimer = window.setTimeout(() => {
        this.dismissSuccess();
      }, this.successMs);
    }

    dismissSuccess() {
      clearTimeout(this.successTimer);
      if (this.state.purchase !== PURCHASE.SUCCESS || this._dismissing) {
        if (this.state.purchase === PURCHASE.SUCCESS) {
          this.finishDismiss();
        }
        return;
      }

      this._dismissing = true;
      this.root.classList.add('is-dismissing');
      this.root.classList.remove('is-visible');
      this.root.setAttribute('aria-hidden', 'true');

      this.bound.onDismissEnd = (event) => {
        if (event.target !== this.root) return;
        if (event.propertyName !== 'opacity' && event.propertyName !== 'transform') return;
        this.root.removeEventListener('transitionend', this.bound.onDismissEnd);
        this.finishDismiss();
      };
      this.root.addEventListener('transitionend', this.bound.onDismissEnd);

      clearTimeout(this.dismissTimer);
      this.dismissTimer = window.setTimeout(() => this.finishDismiss(), DISMISS_FALLBACK_MS);
    }

    finishDismiss() {
      clearTimeout(this.dismissTimer);
      clearTimeout(this.successTimer);
      if (this.bound.onDismissEnd) {
        this.root.removeEventListener('transitionend', this.bound.onDismissEnd);
      }
      this._dismissing = false;
      this.root.classList.remove('is-dismissing', 'is-success');

      // Lock until the shopper returns to the Buy Box, then scrolls past it again.
      this.state.purchase = PURCHASE.IDLE;
      this.state.postSuccessLock = true;
      this.render();
    }

    announce(message) {
      if (!this.els.live || !message) return;
      this.els.live.textContent = '';
      window.requestAnimationFrame(() => {
        this.els.live.textContent = message;
      });
    }

    shouldShowPurchaseChrome() {
      return (
        this.state.visibility === VISIBILITY.BUYBOX_OUT &&
        !this.state.postSuccessLock &&
        this.passesMobileGate()
      );
    }

    shouldShowBar() {
      if (this._dismissing) return false;
      const purchase = this.state.purchase;
      if (purchase === PURCHASE.SUCCESS || purchase === PURCHASE.ADDING) return true;
      if (!this.shouldShowPurchaseChrome()) return false;
      return (
        purchase === PURCHASE.VISIBLE ||
        purchase === PURCHASE.SELECT_VARIANT ||
        purchase === PURCHASE.SOLD_OUT ||
        purchase === PURCHASE.ERROR
      );
    }

    render() {
      const showBar = this.shouldShowBar();
      const success = this.state.purchase === PURCHASE.SUCCESS;

      if (showBar || this._dismissing) {
        this.syncChromeOffset();
      }

      // Keep [hidden] off during dismiss animation so CSS transitions can run;
      // finishDismiss() will hide via shouldShowBar → hidden.
      if (!this._dismissing) {
        this.root.hidden = !showBar;
      }

      const visibleNow = showBar && !this._dismissing;
      this.root.classList.toggle('is-visible', visibleNow);
      this.root.classList.toggle('is-success', success);
      this.root.setAttribute('aria-hidden', visibleNow ? 'false' : 'true');

      if (visibleNow !== this._lastBarShown) {
        this._lastBarShown = visibleNow;
        this.syncChatClearance(visibleNow);
      }

      if (this.els.purchasePanel) this.els.purchasePanel.hidden = success;
      if (this.els.successPanel) this.els.successPanel.hidden = !success;

      this.root.dataset.leraSpbMobileReady = this.state.mobileScrollReady ? '1' : '0';
      this.root.dataset.leraSpbPostSuccessLock = this.state.postSuccessLock ? '1' : '0';

      this.renderPurchaseChrome();
    }

    renderPurchaseChrome() {
      if (!this.els.cta || !this.els.ctaLabel) return;
      const purchase = this.state.purchase;

      this.els.cta.classList.toggle('is-loading', purchase === PURCHASE.ADDING);
      this.els.cta.classList.toggle('is-disabled', purchase === PURCHASE.SOLD_OUT);
      this.els.cta.disabled = purchase === PURCHASE.SOLD_OUT || purchase === PURCHASE.ADDING;

      if (purchase === PURCHASE.ADDING) {
        this.els.ctaLabel.textContent = this.labels.adding;
      } else if (purchase === PURCHASE.SELECT_VARIANT) {
        this.els.ctaLabel.textContent = this.labels.select;
        this.els.cta.disabled = false;
      } else if (purchase === PURCHASE.SOLD_OUT) {
        this.els.ctaLabel.textContent = this.labels.soldOut;
      } else if (purchase === PURCHASE.ERROR) {
        this.els.ctaLabel.textContent = this.labels.atc;
        this.els.cta.disabled = false;
      } else {
        this.els.ctaLabel.textContent = this.labels.atc;
        this.els.cta.disabled = false;
      }
    }
  }

  function boot(scope) {
    const roots = (scope || document).querySelectorAll('lera-smart-purchase-bar.lera-spb, .lera-spb[data-section-id]');
    roots.forEach((root) => {
      if (root._leraSpbController) {
        root._leraSpbController.destroy();
      }
      const controller = new LeraSmartPurchaseController(root);
      root._leraSpbController = controller;
      controller.init();
    });
  }

  function bootWhenReady() {
    if (window.LeraViewportLayer && typeof window.LeraViewportLayer.ensure === 'function') {
      window.LeraViewportLayer.ensure();
    }
    boot();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootWhenReady);
  } else {
    bootWhenReady();
  }

  document.addEventListener('shopify:section:load', (event) => boot(event.target));

  window.LeraSmartPurchaseController = LeraSmartPurchaseController;
  window.LeraSmartPurchaseBar = {
    version: '8.11D.1',
    MOBILE_SCROLL_THRESHOLD,
    refresh: boot,
  };
})();
